import React, { useCallback, useEffect, useState } from "react";
import { Cpu, Download, Pause, Play, Plus, Search } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { downloadCSV, fdate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { EmptyState, LoadingRows } from "@/components/common/EmptyState";
import { StatCard } from "@/components/common/StatCard";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ProcessingForm } from "./ProcessingForm";
import { ProcessingDetail } from "./ProcessingDetail";
import { ProcessingDashboard } from "./ProcessingDashboard";

export const STAGE_TONE = {
  Queued: "neutral", "Data received": "info", Processing: "warning", "Internal QC": "info",
  Rework: "danger", Delivered: "success", "On hold": "neutral", Cancelled: "danger",
};

export const hrs = (h) => `${Number(h || 0).toFixed(1)} h`;

export default function Processing() {
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ types: [], stages: [], engineers: [], complexity: [] });
  const [q, setQ] = useState("");
  const [stage, setStage] = useState("open");
  const [engineer, setEngineer] = useState("all");
  const [serviceType, setServiceType] = useState("all");
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [tab, setTab] = useState("jobs");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/processing", { params: { q, stage, engineer, service_type: serviceType } });
      setRows(data);
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  }, [q, stage, engineer, serviceType]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => { api.get("/processing/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);

  const running = rows.filter((r) => r.running);
  const pending = rows.filter((r) => !["Delivered", "Cancelled"].includes(r.stage));
  const today = new Date().toISOString().slice(0, 10);
  const delayed = pending.filter((r) => r.due_date && r.due_date < today);
  const delivered = rows.filter((r) => r.stage === "Delivered");
  const avgEff = delivered.length
    ? Math.round(delivered.reduce((a, r) => a + Number(r.efficiency || 0), 0) / delivered.length) : 0;

  const toggleTimer = async (row, action) => {
    try {
      await api.post(`/processing/${row.id}/timer`, { action });
      toast.success(action === "start" ? "Timer running" : "Timer paused");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const columns = [
    {
      key: "project", label: "Project",
      render: (r) => (
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold">{r.project_name}</p>
          <p className="num truncate text-xs text-muted-foreground">
            {r.number} &middot; {r.customer_name} &middot; {r.service_type}
          </p>
        </div>
      ),
    },
    { key: "engineer", label: "Engineer", render: (r) => <span className="text-sm">{r.engineer_name}</span>, hideMobile: true },
    {
      key: "size", label: "Data", render: (r) => (
        <span className="num text-xs text-muted-foreground">
          {r.images ? `${r.images} img ` : ""}{r.size_gb ? `${r.size_gb} GB ` : ""}{r.area_sqkm ? `${r.area_sqkm} km²` : ""}
          {!r.images && !r.size_gb && !r.area_sqkm ? "\u2014" : ""}
        </span>
      ), hideMobile: true,
    },
    {
      key: "time", label: "Active / expected", align: "right",
      render: (r) => (
        <span className="num text-sm">
          {hrs(r.active_hours)}<span className="text-muted-foreground"> / {hrs(r.expected_hours)}</span>
        </span>
      ),
    },
    {
      key: "efficiency", label: "Efficiency", align: "right",
      render: (r) => r.active_hours > 0
        ? <span className={`num text-sm font-bold ${r.efficiency >= 100 ? "text-success" : r.efficiency >= 75 ? "text-foreground" : "text-destructive"}`}>{r.efficiency}%</span>
        : <span className="text-xs text-muted-foreground">not started</span>,
    },
    { key: "due", label: "Due", render: (r) => <span className={`text-sm ${r.due_date && r.due_date < today && r.stage !== "Delivered" ? "font-semibold text-destructive" : "text-muted-foreground"}`}>{r.due_date ? fdate(r.due_date) : "\u2014"}</span>, hideMobile: true },
    { key: "stage", label: "Stage", render: (r) => <StatusBadge status={r.stage} tone={STAGE_TONE[r.stage]} /> },
    {
      key: "timer", label: "", align: "right",
      render: (r) => can("processing", "edit") && !["Delivered", "Cancelled"].includes(r.stage) ? (
        <div onClick={(e) => e.stopPropagation()}>
          {r.running ? (
            <Button size="iconSm" variant="outline" title="Pause" data-testid={`pause-${r.number}`}
              onClick={() => toggleTimer(r, "pause")}><Pause className="h-4 w-4 text-warning" /></Button>
          ) : (
            <Button size="iconSm" variant="outline" title="Start" data-testid={`start-${r.number}`}
              onClick={() => toggleTimer(r, "start")}><Play className="h-4 w-4 text-success" /></Button>
          )}
        </div>
      ) : null,
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Cpu}
        title="Data Processing"
        subtitle="Photogrammetry and mapping jobs with real working time, learned benchmarks and fair productivity."
        actions={
          <div className="flex flex-wrap gap-2">
            {rows.length > 0 && can("processing", "export") && (
              <Button variant="outline" data-testid="processing-export-btn" onClick={() => {
                downloadCSV(rows.map((r) => ({
                  Job: r.number, Project: r.project_name, Customer: r.customer_name,
                  Type: r.service_type, Engineer: r.engineer_name, Complexity: r.complexity,
                  Images: r.images, GB: r.size_gb, SqKm: r.area_sqkm, Stage: r.stage,
                  ActiveHours: r.active_hours, ExpectedHours: r.expected_hours,
                  Efficiency: r.efficiency, Rework: r.rework_count, Due: r.due_date,
                })), "processing-jobs.csv");
                toast.success("Exported");
              }}>
                <Download className="h-4 w-4" /> Export
              </Button>
            )}
            {can("processing", "add") && (
              <Button data-testid="new-processing-btn" onClick={() => { setEditing(null); setFormOpen(true); }}>
                <Plus className="h-4 w-4" /> New job
              </Button>
            )}
          </div>
        }
      />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4" data-testid="processing-kpis">
        <StatCard label="Running now" value={running.length} icon="Play" tone="success" testId="kpi-proc-running" />
        <StatCard label="In the queue" value={pending.length} icon="ListChecks" tone="info" testId="kpi-proc-pending" />
        <StatCard label="Past due" value={delayed.length} icon="AlarmClock" tone="danger" testId="kpi-proc-delayed" />
        <StatCard label="Avg efficiency" value={avgEff} icon="Gauge" tone="primary" testId="kpi-proc-efficiency"
          hint={delivered.length ? `across ${delivered.length} delivered job(s)` : "no delivered jobs yet"} />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <div className="scroll-slim -mx-1 max-w-full overflow-x-auto px-1">
          <TabsList className="w-max">
            <TabsTrigger value="jobs" data-testid="proc-tab-jobs">Jobs</TabsTrigger>
            <TabsTrigger value="dashboard" data-testid="proc-tab-dashboard">Productivity</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="jobs" className="mt-5 space-y-4">
          <div className="flex flex-wrap gap-3">
            <div className="relative min-w-[220px] flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="processing-search"
                placeholder="Search by project, customer or job number" />
            </div>
            <Select value={stage} onValueChange={setStage}>
              <SelectTrigger className="w-[180px]" data-testid="processing-stage-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="open">Open only</SelectItem>
                <SelectItem value="all">All stages</SelectItem>
                {(meta.stages || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={serviceType} onValueChange={setServiceType}>
              <SelectTrigger className="w-[190px]" data-testid="processing-type-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All services</SelectItem>
                {(meta.types || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={engineer} onValueChange={setEngineer}>
              <SelectTrigger className="w-[180px]" data-testid="processing-engineer-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All engineers</SelectItem>
                {(meta.engineers || []).map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {loading ? <LoadingRows /> : (
            <DataTable columns={columns} rows={rows} onRowClick={(r) => setDetailId(r.id)}
              rowTestId={(r) => `processing-row-${r.number}`}
              rowClassName={(r) => r.running && "bg-success-soft/30"}
              empty={<EmptyState icon={Cpu} title="No processing jobs yet"
                description="Log the project, the data size and the service, then start the timer when you actually begin."
                action={can("processing", "add") && (
                  <Button data-testid="processing-empty-new-btn" onClick={() => setFormOpen(true)}>
                    <Plus className="h-4 w-4" /> New job
                  </Button>
                )} />} />
          )}
        </TabsContent>

        <TabsContent value="dashboard" className="mt-5">
          <ProcessingDashboard />
        </TabsContent>
      </Tabs>

      <ProcessingForm open={formOpen} job={editing} meta={meta}
        onOpenChange={(v) => { setFormOpen(v); if (!v) setEditing(null); }}
        onSaved={(id) => { load(); if (id) setDetailId(id); }} />
      <ProcessingDetail jobId={detailId} open={!!detailId} meta={meta}
        onOpenChange={(v) => !v && setDetailId(null)}
        onEdit={(job) => { setEditing(job); setDetailId(null); setFormOpen(true); }}
        onChanged={load} />
    </div>
  );
}
